<template>
    <div
        v-if="bookInsight.id"
        class="book-insight-view"
        :class="{ 'hidden' : showReaderMode}"
    >
        <subscribe-bi />
        <start-membership />
        <main>
            <section class="container">
                <div class="row align-items-center bi-actions">
                    <div class="col-auto">
                        <router-link :to="{ name : 'bi-discover' }" class="bi-go-back">
                            <svg width="20" height="20" viewBox="0 0 24 24">
                                <path fill="#FFFFFF" fill-rule="evenodd" d="M21.679 11.173l-6.334-6.8c-.429-.39-1.252-.551-1.732-.1-.472.443-.458 1.304.013 1.75l4.453 4.775H3.187c-.655 0-1.187.537-1.187 1.2 0 .663.532 1.2 1.187 1.2H18.08l-4.453 4.775c-.406.41-.48 1.301-.013 1.75.468.45 1.32.306 1.732-.1l6.334-6.8c.216-.233.322-.506.321-.825-.011-.286-.127-.617-.321-.825z" />
                            </svg>
                            What's New
                        </router-link>
                    </div>
                    <div class="col-auto ml-auto">
                        <share-dropdown :networks="$_networks" :network-events="$_networkEvents" />
                    </div>
                </div>
                <section class="hero">
                    <!-- TODO: only pass book insight instead of mapping it's props or use vuex -->
                    <book-insight-hero
                        :book-insight="bookInsight"
                        :title="bookInsight.title"
                        :authors="bookInsight.authors"
                        :is-featured="isFeatured"
                        :background-color="$_backgroundColor"
                        :background-shape="$_backgroundShape"
                        @read-button-clicked="openReaderMode"
                    />
                    <h4> {{ bookInsight.short_summary }} </h4>
                </section>
                <section v-if="!isEmpty(bookInsight.comment) && bookInsight.comment.id">
                    <h1>Why We Like This Book</h1>
                    <bookinsight-comment
                        :comment="bookInsight.comment"
                    />
                </section>
                <section v-if="bookInsight.themes.length">
                    <h1>Key Themes</h1>
                    <bookinsight-themes :is-freemium="isFreemium" :themes="bookInsight.themes" @open-reader="openReaderOnSelectedTheme" />
                </section>
                <section>
                    <h1 class="d-flex align-items-center">
                        At a Glance
                        <span v-if="isFeatured">
                            <bi-featured :date="bookInsight.featured_at" class="ml-3" />
                        </span>
                    </h1>
                    <bookinsight-summary :bookinsight="bookInsight" />
                </section>
                <section v-if="bookInsight.similar.length" class="similiar-book-insights">
                    <h1>Similar Book Insights</h1>
                    <bi-carousel :books-insights="bookInsight.similar" />
                </section>
                <!-- <section v-if="!isEmpty(originalAudiobook) && originalAudiobook.id" class="listen-to-original-audiobook">
                    <h1 class="text-center">
                        Listen to the Original Audiobook
                    </h1>
                    <audiobook-card
                        :audiobook-data="originalAudiobook"
                        :book-insight="bookInsight"
                    />
                </section> -->
            </section>
        </main>
        <memod-footer />
    </div>
</template>

<script>

import _isEmpty from "lodash/isEmpty";
import _cloneDeep from "lodash/cloneDeep";
import { mapState, mapGetters } from "vuex";
import biBackgroundMixins from "@/mixins/biBackgroundMixins";
import biSocialShareMixins from "@/mixins/biSocialShareMixins";

export default {
    name: "BookInsight",
    metaInfo() {
        return {
            titleTemplate: !_isEmpty(this.bookInsight) ? `${this.bookInsight.title} by ${this.bookInsight.authors[0].name} - %s` : "",
            meta: [
                {
                    hid: "og:title",
                    name: "og:title",
                    content: this.metaTitle,
                    vmid: "og:title"
                },
                {
                    hid: "og:type",
                    name: "og:type",
                    content: "article",
                    vmid: "og:type"
                },
                {
                    hid: "og:description",
                    name: "og:description",
                    content: this.metaDescription,
                    vmid: "og:description"
                },
                {
                    hid: "twitter:title",
                    name: "twitter:title",
                    content: this.metaTitle,
                    vmid: "twitter:title"
                },
                {
                    hid: "twitter:descripton",
                    name: "twitter:description",
                    content: this.metaDescription,
                    vmid: "twitter:description"
                },
                {
                    hid: "article:published_time",
                    name: "article:published_time",
                    content: this.bookInsight.published_at,
                    vmid: "article:published_time"
                }
            ]
        }
    },
    components: {
        bookInsightHero: () => import(/* webpackChunkName: "book-insight-hero"*/ "@c/books-insights/hero"),
        biCarousel: () => import(/* webpackChunkName: "bi-carousel"*/ "@c/books-insights/carousel"),
        // audiobookCard: () => import(/* webpackChunkName: "audiobook-card"*/ "./audiobook-card"),
        biFeatured: () => import(/* webpackChunkName: "bi-featured"*/ "@c/books-insights/featured"),
        memodFooter: () => import(/* webpackChunkName: "memod-footer"*/ "@v/memod-footer"),
        subscribeBi: () => import(/* webpackChunkName: "subscribe-bi"*/ "@c/books-insights/modals/subscribe-bi"),
        startMembership: () => import(/* webpackChunkName: "start-membership"*/ "@c/books-insights/modals/start-membership"),
        bookinsightComment: () => import(/* webpackChunkName: "bookinsight-comment"*/ "@v/book-insight/bookinsight-comment"),
        bookinsightThemes: () => import(/* webpackChunkName: "bookinsight-themes"*/ "@v/book-insight/bookinsight-themes"),
        bookinsightSummary: () => import(/* webpackChunkName: "bookinsight-summary"*/ "@v/book-insight/bookinsight-summary"),
        shareDropdown: () => import(/* webpackChunkName: "share-dropdown"*/ "@c/books-insights/share-dropdown")
    },
    mixins: [biBackgroundMixins, biSocialShareMixins],
    data() {
        return {
            bookInsight: {},
            similarBookinsights: [],
            originalAudiobook: {},
            nonfictionBaseUrl: process.env.VUE_APP_NONFICTION_BASE_API_URL,
            currentUrl: `${location.origin}${this.$route.fullPath}`
        };
    },
    computed: {
        isFeatured() {
            return Boolean(this.bookInsight.featured_at);
        },
        isPremium() {
            return this.bookInsight.access_type == this.accessTypeIds.PREMIUM;
        },
        isFreemium() {
            return this.bookInsight.access_type == this.accessTypeIds.FREEMIUM;
        },
        ...mapState({
            showReaderMode: state => state.NonfictionReader.showReaderMode,
            readerThemes: state => state.NonfictionReader.readerThemes,
            audioSources: state => state.NonfictionPlayer.audioSources,
            accessTypeIds: state => state.AccessTypes.idList
        }),
        bookInsightUrl() {
            return location.origin + this.$route.path;
        },
        ...mapGetters({
            nonFictionSubscriptionDaysLeft: "UserMembership/getNonFictionSubscriptionDaysLeft",
            isLoggedIn: "User/isLoggedIn"
        }),
        canOpenReaderMode() {
            return !(this.nonFictionSubscriptionDaysLeft <= 0 && this.isPremium);
        },
        metaTitle() {
            return `${this.bookInsight.title} by ${this.bookInsight.authors && this.bookInsight.authors[0].name}`;
        },
        metaDescription() {
            return this.bookInsight.meta_description ? this.bookInsight.meta_description : this.bookInsight.short_summary;
        }
    },
    watch: {
        "$route": {
            handler({ name }) {
                this.fetchAll(name);
            },
            immediate: true
        },
        showReaderMode(current) {
            if (!current) {
                scrollTo(0, 0);
            }
        }
    },
    beforeDestroy() {
        this.$store.dispatch("NonfictionPlayer/resetTotalElapsedTimeDuration");
    },
    methods: {
        fetchAll(name) {
            if (_isEmpty(this.bookInsight)) {
                this.fetchBookInsight().then(() => {
                    if (name === "book-insight-reader" && !this.showReaderMode) {
                        this.openReaderMode();
                    }
                    this.fetchOriginalAudiobook();
                });
            } else if (this.bookInsight.slug !== this.$route.params.slug) {
                this.fetchBookInsight().then(() => {
                    if (name === "book-insight-reader" && !this.showReaderMode) {
                        this.openReaderMode();
                    }
                    this.fetchOriginalAudiobook();
                });
            } else if (this.bookInsight.slug == this.$route.params.slug) {
                if (name === "book-insight-reader" && !this.showReaderMode) {
                    this.openReaderMode();
                }
            }
        },
        fetchBookInsight() {
            this.$store.dispatch("Application/showLoader", true);
            const bookInsightSlug = this.$route.params.slug;
            if (!bookInsightSlug) {
                this.$router.replace({ name: "404" });
                return;
            }

            const url = `${this.nonfictionBaseUrl}/site/book-insights/${bookInsightSlug}`;
            return axiosCms({
                url,
                method: "GET"
            }).then((response) => {
                this.$store.dispatch("NonfictionReader/formatThemes", response.data.themes);
                this.$store.dispatch("NonfictionReader/setBookInsight", response.data);
                this.bookInsight = response.data;

                this.trackBookInsight();

                if (this.isLoggedIn && process.env.NODE_ENV === "production" && window.location.hostname.replace("www.", "") === "hibooks.com") {
                    Leanplum.track("web_bi_insight_opened", {
                        "insight_id": this.bookInsight.id,
                        "insight_title": this.bookInsight.title,
                        "insight_author": this.bookInsight.authors[0].name
                    });
                }
            }).catch((e) => {
                console.log(e)
                this.$router.replace({ name: "404" });
            }).finally(() => {
                this.$store.dispatch("Application/showLoader", false);
            });
        },
        isEmpty(object) {
            return _isEmpty(object);
        },
        fetchOriginalAudiobook() {
            const audiobookId = this.bookInsight.external_book ? this.bookInsight.external_book.external_id : false;
            if (!audiobookId) {
                return;
            }

            const requestUrl = `v2/audiobook/${audiobookId}`;
            return axios({
                url: requestUrl,
                method: "GET"
            }).then((response) => {
                this.originalAudiobook = response.data;
                this.$store.dispatch("NonfictionReader/setOriginalAudiobook", response.data);
            });
        },

        async openReaderOnSelectedTheme(index) {
            if (!this.canOpenReaderMode) {
                this.$modal.show("start-membership");
                return;
            }

            await this.openReaderMode();
            await this.$nextTick(() => {
                const TIME_TO_WAIT_FOR_READER_ANIMATION_TO_END = 300;
                setTimeout(() => {
                    const themeToFocus = document.getElementById(`theme-block-${index + 1}`);
                    themeToFocus.scrollIntoView();
                    this.$store.dispatch("NonfictionReader/setSelectedReaderTheme", index);
                    const nextPlayerAudioIndex = this.getAudioSourceIndexById(this.readerThemes[index].id);
                    if (this.readerThemes[index].hasAudio && nextPlayerAudioIndex >= 0) {
                        this.$store.dispatch("NonfictionPlayer/setSelectedAudioSource", nextPlayerAudioIndex);
                    }
                }, TIME_TO_WAIT_FOR_READER_ANIMATION_TO_END);
            });
        },
        async openReaderMode() {
            if (!this.canOpenReaderMode) {
                return;
            }

            await this.setReaderAndPlayerData();
            await this.$store.dispatch("NonfictionReader/setShowReaderMode", true);
            await scrollTo(0, 0);
            if (this.isLoggedIn && process.env.NODE_ENV === "production" && window.location.hostname.replace("www.", "") === "hibooks.com") {
                Leanplum.track("web_bi_insight_read");
            }
            await this.$router.push({ name: "book-insight-reader" });
        },
        async setReaderAndPlayerData() {
            await this.$store.dispatch("Application/showLoader", true);
            const bookInsight = _cloneDeep(this.bookInsight);

            await this.$store.dispatch("NonfictionPlayer/formatSources", bookInsight.themes);
            await this.$store.dispatch("NonfictionPlayer/setBookInsight", bookInsight);
            await this.$store.dispatch("Application/showLoader", false);

            if (this.isLoggedIn && process.env.NODE_ENV === "production" && window.location.hostname.replace("www.", "") === "hibooks.com") {
                Leanplum.track("web_bi_insight_listened", {
                    "insight_id": this.bookInsight.id,
                    "insight_title": this.bookInsight.title,
                    "insight_author": this.bookInsight.authors[0].name
                });
            }
        },
        getAudioSourceIndexById(id) {
            return this.audioSources.findIndex(item => item.id == id);
        },
        trackBookInsight() {
            const gtmEvent = {
                event: "insight-pageview",
                action: "insight-pageview",
                insightSlug: this.bookInsight.slug,
                insightGenre: this.bookInsight.genre.name
            }

            this.$_gtmTrackEvent(gtmEvent);
        }
    }
}
</script>

<style lang="scss">
.book-insight-view {
    h1 {
        color: #2C2C2C;
        font-size: 27px;
        font-weight: 600;
        line-height: 29px;
        margin-bottom: 20px;
    }

    section {
        margin-bottom: 50px;

        @media (max-width: $md) {
            margin-bottom: 40px !important;
        }
    }

    .container {
        overflow: hidden;

        .share-dropdown {
            .bp-dropdown__btn {
                .btn {
                    padding: 8px;
                }
            }
        }
    }

    .bi-actions {
        margin-bottom: 30px;

        .bi-go-back {
            color: white;
            font-size: 15px;
            font-weight: 600;
            font-style: normal;
            font-stretch: normal;
            line-height: normal;
            letter-spacing: 0.4px;
            display: flex;
            align-items: center;
            justify-content: center;
            text-decoration: none;

            svg {
                margin-right: 8px;
                transform: rotate(180deg);
            }
        }
    }

    .hero {

        h4 {
            color: white;
            font-size: 23px;
            line-height: 32px;
            text-align: center;
            margin: 40px 120px;

            @media (max-width: $lg) {
                font-size: 18px;
                line-height: 24px;
                margin: 20px 100px 50px 100px;
            }

            @media (max-width: $md) {
                margin: 20px 0 40px 0;
            }
        }
    }

    .similiar-book-insights {
        .bi-carousel {
            .swiper-container {
                @media (max-width: 470px) {
                    width: 120%;
                }

                @media (max-width: 400px) {
                    width: 135%;
                }

                @media (max-width: 350px) {
                    width: 160%;
                }
            }
        }
    }

    .listen-to-original-audiobook {
        margin-bottom: 100px !important;

        @media (max-width: $md) {
            margin-bottom: 40px !important;

            h1 {
                margin-bottom: 20px;
            }
        }
    }

    &.hidden {
        overflow: hidden;
        height: 100vh;
    }
}
</style>
